import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  Icon,
  IDropdownOption,
  mergeStyles,
  mergeStyleSets,
  TextField,
  Toggle,
} from "@fluentui/react";
import { toast } from "components/FluentToast";
import { FormSpinner } from "components/FormSpinner";
import { ClearButton, Flex, StyledPrimaryButton } from "components/styled";
import { useCreateAttachment } from "hooks/data/mutations/useCreateAttachment";
import usePhraseActivationStatus from "hooks/usePhraseActivationStatus";
import React, { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Attachment } from "types/types";
import { toBase64 } from "utils/utils";
import { useSelector } from "../../store/hooks";

// have to match BFMCommonConstants.ATTACHMENT_MAX_SIZE_IN_MB / ATTACHMENT_COMMENT_MAX_LENGTH on the backend
export const ATTACHMENT_MAX_SIZE_IN_MB = 20;
export const ATTACHMENT_COMMENT_MAX_LENGTH = 2048;

type SelectedFile = {
  name: string;
  contentType: string;
  size: number;
  base64str: string;
};

type Props = {
  fleetId: number;
  vehicleId: number | null; // null = fleet level document
  onUploaded: (attachment: Attachment) => void;
  close: () => void;
};

const UploadAttachmentDialog = ({
  fleetId,
  vehicleId,
  onUploaded,
  close,
}: Props) => {
  const { t } = useTranslation();
  const phraseActivated = usePhraseActivationStatus().activated;
  const taxonomy = useSelector((s) => s.taxonomy);

  // vehicle documents use the AttachmentType.VEHICLE_* types, fleet level documents the AttachmentType.FLEET_* types
  const typePrefix =
    vehicleId != null ? "AttachmentType.VEHICLE_" : "AttachmentType.FLEET_";
  const typeOptions: IDropdownOption[] = useMemo(
    () =>
      taxonomy.AttachmentType.items
        .filter((tax) => tax.code.startsWith(typePrefix))
        .map((tax) => ({
          key: tax.id,
          text: t(tax.code),
        })),
    [taxonomy.AttachmentType.items, typePrefix, t],
  );

  const [file, setFile] = useState<SelectedFile | null>(null);
  const [attachmentTypeCode, setAttachmentTypeCode] = useState<number | null>(
    (typeOptions[0]?.key as number) ?? null,
  );
  // clients never see the fleet details, so fleet level documents are never visible for them
  const [isVisibleForClient, setIsVisibleForClient] = useState(
    vehicleId != null,
  );
  const [attachmentComment, setAttachmentComment] = useState("");

  const { mutate: onCreateAttachment, isLoading } = useCreateAttachment();

  const classes = getClassNames();

  const onSubmit = () => {
    if (!file || attachmentTypeCode === null) return;
    onCreateAttachment(
      {
        fleetId,
        body: {
          vehicleId,
          attachmentTypeCode,
          isVisibleForClient,
          attachmentComment: attachmentComment.trim() || null,
          name: file.name,
          contentType: file.contentType,
          base64str: file.base64str,
        },
      },
      {
        onSuccess: (res) => {
          toast.success(t("bfm.success"));
          onUploaded(res.data);
          close();
        },
      },
    );
  };

  return (
    <Dialog
      hidden={false}
      modalProps={{
        isBlocking: phraseActivated ? false : true,
      }}
      dialogContentProps={{
        type: DialogType.normal,
        title: t("bfm.attachments.upload.label"),
        showCloseButton: true,
      }}
      minWidth={450}
      onDismiss={close}
    >
      <div className={classes.main}>
        <Dropdown
          label={t("bfm.attachments.type.label")}
          options={typeOptions}
          selectedKey={attachmentTypeCode}
          onChange={(_ev, option) =>
            setAttachmentTypeCode(option ? (option.key as number) : null)
          }
          required
        />
        {vehicleId != null && (
          <Toggle
            className={classes.fieldItem}
            label={t("bfm.attachments.visibleForClient.label")}
            checked={isVisibleForClient}
            onText={t("greco.yes")}
            offText={t("greco.no")}
            onChange={(_ev, checked) => setIsVisibleForClient(!!checked)}
          />
        )}
        <TextField
          className={classes.fieldItem}
          label={t("bfm.comment.label")}
          multiline
          rows={3}
          maxLength={ATTACHMENT_COMMENT_MAX_LENGTH}
          value={attachmentComment}
          onChange={(_ev, value) => setAttachmentComment(value ?? "")}
        />
        <div className={classes.fieldItem}>
          <AttachmentDropzone file={file} setFile={setFile} />
        </div>
        <div className={classes.hint}>
          {t("bfm.attachments.maxSize.label", {
            size: ATTACHMENT_MAX_SIZE_IN_MB,
          })}
        </div>
        <DialogFooter className={classes.footer}>
          <StyledPrimaryButton
            text={t("bfm.upload.label")}
            iconProps={{ iconName: "Upload" }}
            disabled={!file || attachmentTypeCode === null || isLoading}
            onClick={onSubmit}
          />
          <DefaultButton onClick={close} text={t("bfm.close.label")} />
        </DialogFooter>
        {isLoading && <FormSpinner />}
      </div>
    </Dialog>
  );
};

const AttachmentDropzone = ({
  file,
  setFile,
}: {
  file: SelectedFile | null;
  setFile: (file: SelectedFile | null) => void;
}) => {
  const { t } = useTranslation();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const selected = acceptedFiles[0];
      if (!selected) return;
      if (selected.size > ATTACHMENT_MAX_SIZE_IN_MB * 1024 * 1024) {
        toast.error(
          t("bfm.attachments.maxSize.label", {
            size: ATTACHMENT_MAX_SIZE_IN_MB,
          }),
        );
        return;
      }
      toBase64(selected).then((base64str) => {
        setFile({
          name: selected.name,
          contentType: selected.type || "application/octet-stream",
          size: selected.size,
          base64str,
        });
      });
    },
    [setFile, t],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject, open } =
    useDropzone({
      noDragEventsBubbling: true,
      noClick: true,
      noKeyboard: true,
      multiple: false,
      onDrop,
    });

  return (
    <Root
      className={mergeStyles({
        accept: isDragAccept,
        reject: isDragReject,
      })}
      {...getRootProps()}
    >
      <input {...(getInputProps() as any)} />
      {file ? (
        <Flex direction="column" gap={10} style={{ width: "100%" }}>
          <Flex
            style={{ padding: "0 20px", width: "100%", overflow: "hidden" }}
            gap={5}
            justify="center"
          >
            <DocumentIcon iconName="Document" />
            <FileName title={file.name}>{file.name}</FileName>
            <ClearButton
              iconProps={{ iconName: "Cancel" }}
              onClick={() => setFile(null)}
            />
          </Flex>
          <DefaultButton
            text={t("bfm.chooseDifferentFile")}
            onClick={() => open()}
          />
        </Flex>
      ) : (
        <Flex direction="column" gap={5}>
          <UploadIcon iconName="CloudUpload" />
          <TitleContainer>
            <Title>{t("bfm.dragFileHere")}</Title>
            <Title>{t("or")}</Title>
          </TitleContainer>
          <DefaultButton text={t("bfm.browseFiles")} onClick={open} />
        </Flex>
      )}
    </Root>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    main: {},
    fieldItem: {
      width: "100%",
      marginTop: 15,
    },
    hint: {
      marginTop: 5,
      fontSize: 12,
      fontStyle: "italic",
    },
    footer: {
      marginTop: 20,
    },
  });

const Root = styled.div<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${(p) => p.theme.palette.neutralTertiaryAlt};
  height: auto;
  padding: 20px;
  margin: 0px;
  font-size: 18px;
  font-weight: 600;
  flex-direction: column;
  user-select: none;
  &:focus {
    outline: none;
  }
  &.accept {
    background-color: ${(p) => p.theme.palette.themeLighterAlt};
  }
  &.reject {
    background-color: ${(p) => p.theme.palette.redDark};
  }
`;

const TitleContainer = styled.div`
  text-align: center;
`;

const FileName = styled.div`
  font-size: 18px;
  color: ${(p) => p.theme.palette.neutralPrimary};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Title = styled.div`
  font-size: 18px;
  color: ${(p) => p.theme.palette.neutralPrimary};
`;

const UploadIcon = styled(Icon)`
  margin-left: 10px;
  vertical-align: bottom;
  color: ${(p) => p.theme.palette.neutralPrimary};
`;

const DocumentIcon = styled(Icon)`
  font-size: 25px;
  color: ${(p) => p.theme.palette.themePrimary};
`;

export default UploadAttachmentDialog;
